import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  FileText, Download, Calendar, Building2, Users, BarChart3,
  Loader2, FileSpreadsheet, FileDown, CheckCircle2, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMReports() {
  const { token, isAdmin } = useCRMAuth();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [reportData, setReportData] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClient && selectedMonth) {
      fetchReportData();
    }
  }, [selectedClient, selectedMonth]);

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/crm/clients/?status=active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setClients((await res.json()).clients);
      }
    } catch (err) {}
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const clientParam = selectedClient !== 'all' ? `&client_id=${selectedClient}` : '';
      const res = await fetch(`${API}/api/crm/reports/monthly?month=${selectedMonth}${clientParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setReportData(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setGenerating(true);
    try {
      const clientParam = selectedClient !== 'all' ? `&client_id=${selectedClient}` : '';
      const res = await fetch(`${API}/api/crm/reports/export?month=${selectedMonth}${clientParam}&format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${selectedMonth}.${format}`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Report exported as ${format.toUpperCase()}`);
      } else {
        toast.error('Export failed');
      }
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setGenerating(false);
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        value: d.toISOString().slice(0, 7),
        label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
    }
    return options;
  };

  return (
    <CRMLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500">Generate and export production reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('xlsx')} disabled={generating || !reportData}>
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button onClick={() => handleExport('pdf')} disabled={generating || !reportData} className="bg-indigo-600 hover:bg-indigo-700">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Client</label>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger>
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Month</label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger>
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getMonthOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Report Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : !reportData ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Report Data</h3>
          <p className="text-slate-500">Select a client and month to generate report</p>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <p className="text-sm text-slate-500">Total Work Items</p>
              <p className="text-2xl font-bold text-slate-900">{reportData.summary?.total_items || 0}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-slate-500">Total Value</p>
              <p className="text-2xl font-bold text-emerald-600">₹{(reportData.summary?.total_value || 0).toLocaleString()}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-slate-500">Tasks Completed</p>
              <p className="text-2xl font-bold text-blue-600">{reportData.summary?.tasks_completed || 0}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-slate-500">Team Members</p>
              <p className="text-2xl font-bold text-purple-600">{reportData.summary?.team_members || 0}</p>
            </Card>
          </div>

          {/* Work Type Breakdown */}
          <Card className="p-5 mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">Production by Work Type</h3>
            {(!reportData.by_work_type || reportData.by_work_type.length === 0) ? (
              <p className="text-sm text-slate-500 text-center py-4">No work logged this month</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-2 px-4 text-sm font-semibold text-slate-600">Work Type</th>
                      <th className="text-left py-2 px-4 text-sm font-semibold text-slate-600">Category</th>
                      <th className="text-right py-2 px-4 text-sm font-semibold text-slate-600">Quantity</th>
                      <th className="text-right py-2 px-4 text-sm font-semibold text-slate-600">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.by_work_type.map((item, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="py-3 px-4 font-medium">{item.work_type_name}</td>
                        <td className="py-3 px-4 text-slate-500">{item.category_name || '-'}</td>
                        <td className="py-3 px-4 text-right">{item.quantity}</td>
                        <td className="py-3 px-4 text-right font-medium text-emerald-600">₹{item.value?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Team Contribution */}
          <Card className="p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Team Contribution</h3>
            {(!reportData.by_employee || reportData.by_employee.length === 0) ? (
              <p className="text-sm text-slate-500 text-center py-4">No team data available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reportData.by_employee.map((emp, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-medium">
                        {emp.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{emp.name}</p>
                        <p className="text-xs text-slate-500">{emp.items} items</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Value:</span>
                      <span className="font-medium text-emerald-600">₹{emp.value?.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </CRMLayout>
  );
}
